import { lazy, Suspense } from 'react';

import { Route, Routes, Navigate } from 'react-router-dom';

import { PageLoader } from '@elements';
import { MantineLayout } from '@layout';
import MantineErrorBoundary from '@utils/MantineErrorBoundary';

import { LayoutRoute } from './LayoutRoute';
import { LazyAccessControl } from './LazyAccessControl';

const MantineComponents = lazy(() => import('@views/MantineComponents'));
const CustomerRequirements = lazy(
  () => import('@views/common/customer-requirements')
);
const HealthCustomerRequirements = lazy(
  () => import('@views/health/customer-requirements')
);
const RequirementQuestionnaire = lazy(
  () => import('@views/common/requirement-questionnaire')
);
const VerifyPolicyDocuments = lazy(
  () => import('@views/term/verify-policy-documents')
);
const Home = lazy(() => import('@views/Home'));
const TermPolicyStatus = lazy(() => import('@views/term/status'));
const NextSteps = lazy(() => import('@views/term/next-steps'));
const HealthNextSteps = lazy(() => import('@views/health/next-steps'));
const HealthPolicyStatus = lazy(() => import('@views/health/status'));
const HealthKYCBox = lazy(() => import('@views/health/kyc'));
const FastQuotes = lazy(() => import('@views/common/FastQuotes'));

const PortingDocuments = lazy(() => import('@views/health/PortingDocuments'));

const AdaptiveProposalV3 = lazy(() => import('@views/common/AdaptiveProposalV3'));
const TermMedicals = lazy(() => import('@views/term/medicals'));

const HealthQuoteV3Fast = lazy(() => import('@views/health/quotev3/fast'));

const TermQuoteV3Fast = lazy(() => import('@views/term/quotev3/fast'));

// account
const Login = lazy(() => import('@views/account/Login'));
const Account = lazy(() => import('@views/account'));

const OptimaSecureMigration = lazy(
  () => import('../views/health/optimaSecureMigrationV3')
);

const ProposalPrintSummary = lazy(
  () => import('@views/health/ProposalPrintSummary')
);

const AppRoutes = () => {
  return (
    <MantineLayout>
      <MantineErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Print route — outside LayoutRoute to avoid GlobalLoginModal */}
            <Route
              path="/health/proposal/print/:insurer/:product"
              element={<ProposalPrintSummary />}
            />
            <Route element={<LayoutRoute />}>
              <Route path="/" element={<Home />} />
              <Route path="/testing-mantine" element={<MantineComponents />} />
              {/* Health Fast Quote */}
              <Route path="/health/fq/*" element={<HealthQuoteV3Fast />} />

              {/* OR to OS Migration */}
              <Route
                path="/health/optima-secure-migration/*"
                element={<OptimaSecureMigration />}
              />

              <Route path="/term/fq/*" element={<TermQuoteV3Fast />} />

              <Route
                path="/health/status/:policyId"
                element={<HealthPolicyStatus />}
              />

              <Route
                path="/health/complete-kyc/:policyID"
                element={<HealthKYCBox />}
              />

              {/* Proposal Form Refactor routes */}
              <Route
                path="/health/proposal/:insurer/:product/:policyId"
                element={<AdaptiveProposalV3 />}
              />
              <Route
                path="/term/proposal/:insurer/:product/:policyId"
                element={<AdaptiveProposalV3 />}
              />
              <Route
                path="/health/porting-docs/:policyId"
                element={<PortingDocuments />}
              />

              {/* Term - Customer Requirement Doc Upload route */}
              <Route
                path="/term/docs/:insurer/:product/:policyId"
                element={<CustomerRequirements />}
              />

              {/* Health - Customer Requirement Doc Upload route */}
              <Route
                path="/health/docs/:insurer/:product/:policyId"
                element={<HealthCustomerRequirements />}
              />

              <Route
                path="/term/:insurer/:product/:policyId/requirement/:requirementId/questionnaire/:questionnaireDocumentTypeId"
                element={<RequirementQuestionnaire />}
              />

              {/* Health NSTP - next steps */}
              <Route
                path="/health/next-steps/:policyId"
                element={<HealthNextSteps />}
              />
              {/* Term NSTP - next steps */}
              <Route path="/term/next-steps/:policyId" element={<NextSteps />} />

              {/* Account */}
              <Route path="/login" element={<Login />} />
              <Route
                path="/account"
                element={
                  <LazyAccessControl>
                    <Account />
                  </LazyAccessControl>
                }
              />

              <Route path="/fq" element={<FastQuotes />} />
              <Route path="/fq/:vertical" element={<FastQuotes />} />

              {/*Term Status Page */}
              <Route path="/term/status/:policyId" element={<TermPolicyStatus />} />

              {/* Policy Documents Verification route - applicable only for BALIC for now */}
              <Route
                path="/term/verify-documents/:insurer/:product/:policyId"
                element={<VerifyPolicyDocuments />}
              />
              <Route
                path="/term/medicals/:insurer/:product/:policyId"
                element={<TermMedicals />}
              />

              {/* Catch all - redirect to home if no match is found */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </MantineErrorBoundary>
    </MantineLayout>
  );
};

export default AppRoutes;
